"use client"

import React, { useMemo, useState, useRef, useEffect } from "react"
import { Remirror, ThemeProvider, useRemirror } from "@remirror/react"
import { BoldExtension } from "@remirror/extension-bold"
import { ItalicExtension } from "@remirror/extension-italic"
import { StrikeExtension } from "@remirror/extension-strike"
import { HeadingExtension } from "@remirror/extension-heading"
import { LinkExtension } from "@remirror/extension-link"
import { MarkdownExtension } from "@remirror/extension-markdown"
import { DocExtension } from "@remirror/extension-doc"
import { CustomVideoExtension } from "../../../../../../components/FormikForm/RichTextEditor/components/RichVideoExtension"
import { CustomImageExtension } from "../../../../../../components/FormikForm/RichTextEditor/components/RichImageExtension"
import { RemirrorPreviewStyles } from "./styles"
import { ParagraphExtension } from "@remirror/extension-paragraph"
import { TextExtension } from "@remirror/extension-text"
import { AttachedFile } from "../../../../../../types/attached-file"
import { CustomAudioExtension } from "../../../../../../components/FormikForm/RichTextEditor/components/RichAudioExtension"
import { CustomPdfExtension } from "../../../../../../components/FormikForm/RichTextEditor/components/RichPdfExtension"
import { CustomLinkExtension, CustomLinkMarkExtension } from "../../../../../../components/FormikForm/RichTextEditor/components/CustomLinkExtension"
import { RichInstagramReelsExtension } from "../../../../../../components/FormikForm/RichTextEditor/components/RichInstagramReelsExtension"

type ProseMirrorNode = { type: string;[key: string]: any };
type DocNode = { type: "doc"; content: ProseMirrorNode[] };

// Type guard para DocNode
function isDocNode(obj: unknown): obj is DocNode {
  return (
    !!obj &&
    typeof obj === "object" &&
    (obj as any).type === "doc" &&
    Array.isArray((obj as any).content)
  );
}


interface RemirrorPreviewProps {
  content: string | ProseMirrorNode[] | DocNode;
  className?: string
  bodyMediaFilesMap?: Record<number, AttachedFile>
  isOwner?: boolean
  block_files_download?: boolean
}

export const RemirrorPreview: React.FC<RemirrorPreviewProps> = ({ content, bodyMediaFilesMap, isOwner = false, className = "", block_files_download = true }) => {

  console.log('🔄 RemirrorPreview received content:', typeof content, content ? 'has content' : 'no content');

  const parsedContent = useMemo<DocNode | undefined>(() => {
    if (typeof content === "string") {
      try {
        const parsed = JSON.parse(content);
        if (isDocNode(parsed)) return parsed;
        if (Array.isArray(parsed)) return { type: "doc", content: parsed };
        return undefined;
      } catch (e) {
        console.error("Error parsing Remirror content:", e);
        return undefined;
      }
    }

    if (isDocNode(content)) {
      return content;
    }

    if (Array.isArray(content)) {
      return { type: "doc", content } as DocNode;
    }

    return undefined;
  }, [content]);


  // Configurar las extensiones necesarias para la preview
  const extensions = useMemo(
    () => [

      new DocExtension({}),
      new ParagraphExtension(),
      new TextExtension(),
      new MarkdownExtension({}),
      new BoldExtension({}),
      new ItalicExtension(),
      new StrikeExtension(),
      new HeadingExtension({ levels: [1, 2, 3, 4, 5, 6] }),
      new LinkExtension({
        autoLink: true,
        defaultTarget: '_blank',
      }),
      new CustomLinkMarkExtension(),
      new CustomLinkExtension({ autoLink: true, selectTextOnClick: true }),
      new CustomImageExtension(bodyMediaFilesMap, isOwner, block_files_download),
      new CustomVideoExtension(bodyMediaFilesMap, isOwner, block_files_download),
      new CustomAudioExtension(bodyMediaFilesMap, isOwner, block_files_download),
      new CustomPdfExtension(bodyMediaFilesMap, isOwner, block_files_download),
      new RichInstagramReelsExtension(),
    ] as any,
    [bodyMediaFilesMap],
  )

  // Función para procesar contenido inválido
  const processInvalidContent = (content: any): any => {
    if (!content || typeof content !== 'object') return content;

    // Si es un array, procesar cada elemento
    if (Array.isArray(content)) {
      return content.map(item => processInvalidContent(item));
    }

    // Si es un nodo customLink, convertirlo a un nodo de texto con mark de link
    if (content.type === 'customLink') {
      return {
        type: 'text',
        text: content.attrs?.text || content.attrs?.href || 'Link',
        marks: [{
          type: 'link',
          attrs: {
            href: content.attrs?.href || '',
            target: '_blank'
          }
        }]
      };
    }

    // Si tiene contenido, procesar recursivamente
    if (content.content && Array.isArray(content.content)) {
      return {
        ...content,
        content: content.content.map((item: any) => processInvalidContent(item))
      };
    }

    return content;
  };

  // Procesar el contenido para manejar nodos customLink
  const processedContent = useMemo(() => {
    if (!parsedContent) return undefined;
    return processInvalidContent(parsedContent);
  }, [parsedContent]);

  // Inicializar el editor en modo solo lectura
  const { manager, state } = useRemirror({
    extensions,
    content: processedContent ?? undefined,
    stringHandler: "markdown",
    onError: (error) => {
      console.warn('Remirror error:', error);
      // Si hay error, intentar procesar el contenido inválido
      if ('message' in error && typeof error.message === 'string' && error.message.includes('customLink')) {
        console.log('Attempting to process invalid customLink content');
        return processInvalidContent(error.invalidContent);
      }
      return error.invalidContent;
    }
  })

  // Update content when it changes
  useEffect(() => {
    if (processedContent && manager) {
      console.log('🔄 Updating Remirror content');
      const { state, dispatch } = manager.view;
      const tr = state.tr.replaceWith(0, state.doc.content.size, manager.schema.nodeFromJSON(processedContent));
      dispatch(tr);
    }
  }, [processedContent, manager]);

  // Add click handler for links
  useEffect(() => {
    const editorView = manager.view;
    if (!editorView) return;

    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "A") {
        e.preventDefault();
        const href = target.getAttribute("href");
        if (href) {
          window.open(href, "_blank", "noopener,noreferrer");
        }
      }
    };

    const dom = editorView.dom;
    dom.addEventListener("click", handler);
    return () => dom.removeEventListener("click", handler);
  }, [manager]);

  if (!processedContent) {
    return <div className="remirror-preview-error">Error loading content</div>
  }

  return (
    <ThemeProvider>
      <RemirrorPreviewStyles />
      <div className={`remirror-preview-container ${className}`}>
        <Remirror
          manager={manager}
          initialContent={state}
          editable={false} //solo lectura
          autoRender="end"
        />
      </div>
    </ThemeProvider>

  )
}

// // Versión que acepta contenido en formato HTML
// export const RemirrorHtmlPreview: React.FC<{ html: string; className?: string }> = ({ html, className = "" }) => {
//   return <div className={`remirror-preview-container ${className}`} dangerouslySetInnerHTML={{ __html: html }} />
// }

// // Versión que acepta contenido en formato Markdown
// export const RemirrorMarkdownPreview: React.FC<{ markdown: string; className?: string }> = ({
//   markdown,
//   className = "",
// }) => {
//   // Configurar las extensiones necesarias para la preview
//   const extensions = useMemo(
//     () => () => [
//       new DocExtension({}),
//       new MarkdownExtension({}),
//       new BoldExtension({}),
//       new ItalicExtension(),
//       new StrikeExtension(),
//       new HeadingExtension({ levels: [1, 2, 3, 4, 5, 6] }),
//       new LinkExtension({ autoLink: true }),
//       new CustomImageExtension({
//         enableResizing: false, // Desactivar resize en modo preview
//       }),
//       new CustomVideoExtension(),
//     ],
//     [],
//   )

//   // Inicializar el editor en modo solo lectura con contenido markdown
//   const { manager, state } = useRemirror({
//     extensions,
//     content: markdown,
//     stringHandler: "markdown",
//   })

//   return (
//     <ThemeProvider>
//       <RemirrorPreviewStyles />
//       <div className={`remirror-preview-container ${className}`}>
//         <Remirror manager={manager} initialContent={state} editable={false} autoRender="end" />
//       </div>
//     </ThemeProvider>
//   )
// }